import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { UserRole } from '../services/users';

interface User {
  id: number;
  username: string;
  email: string;
  full_name: string;
  is_active: boolean;
  is_superuser: boolean;
  company_id?: number;
  company?: {
    id: number;
    name: string;
  };
  role: UserRole;
  permissions: string[];
  twofa_enabled?: boolean;
}

interface LoginResult {
  success: boolean;
  requires2FA?: boolean;
  tempToken?: string;
  errorMessage?: string;
  errorCode?: string;
  status?: number;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<LoginResult>;
  loginWith2FA: (otp: string, tempToken: string) => Promise<boolean>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  updateProfile: (data: any) => Promise<void>;
  setup2FA: () => Promise<any>;
  confirm2FA: (otp: string) => Promise<boolean>;
  disable2FA: (otp: string) => Promise<boolean>;
  requestPasswordReset: (email: string) => Promise<boolean>;
  confirmPasswordReset: (token: string, newPassword: string) => Promise<boolean>;
}

const USER_CACHE_KEY = 'mermaid_user';

const readCachedUser = (): User | null => {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  // Seed from cache so F5 shows the app instantly while refresh runs in background
  const [user, setUserState] = useState<User | null>(() =>
    localStorage.getItem('token') ? readCachedUser() : null
  );
  const [loading, setLoading] = useState(true);

  const setUser = (u: User | null) => {
    setUserState(u);
    if (u) {
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u));
    } else {
      localStorage.removeItem(USER_CACHE_KEY);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      refreshUser();
    } else {
      setLoading(false);
    }

    const handleForceLogout = () => setUser(null);
    window.addEventListener('auth:logout', handleForceLogout);
    return () => window.removeEventListener('auth:logout', handleForceLogout);
  }, []);

  const login = async (username: string, password: string): Promise<LoginResult> => {
    try {
      // Create form data for OAuth2PasswordRequestForm
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);

      const response = await api.post('/api/v2/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.data.requires_2fa) {
        return {
          success: true,
          requires2FA: true,
          tempToken: response.data.temp_token,
        };
      }

      const { access_token, user: userData } = response.data;

      localStorage.setItem('token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      setUser(userData);
      toast.success('Login realizado com sucesso!');
      return {
        success: true,
        requires2FA: false,
      };
    } catch (error: any) {
      const status = error.response?.status;
      const data = error.response?.data || {};
      const detail: string | undefined = data.detail || data.message || undefined;
      const code: string | undefined = data.code || undefined;

      // Não exibir toast de erro no login para evitar mensagens específicas ou duplicadas
      return {
        success: false,
        requires2FA: false,
        errorMessage: detail,
        errorCode: code,
        status,
      };
    }
  };

  const loginWith2FA = async (otp: string, tempToken: string): Promise<boolean> => {
    try {
      const response = await api.post(
        '/api/v2/auth/login/2fa',
        {
          otp,
        },
        {
          headers: {
            Authorization: `Bearer ${tempToken}`,
          },
        },
      );

      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);

      toast.success('Login com 2FA realizado com sucesso!');
      return true;
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro na verificação 2FA';
      toast.error(message);
      return false;
    }
  };

  const setup2FA = async (): Promise<any> => {
    try {
      const response = await api.post('/api/v2/auth/2fa/setup');
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao configurar 2FA';
      toast.error(message);
      throw error;
    }
  };

  const confirm2FA = async (otp: string): Promise<boolean> => {
    try {
      const response = await api.post('/api/v2/auth/2fa/confirm', { otp });

      // Atualizar o usuário buscando dados atualizados do backend
      await refreshUser();

      toast.success('2FA habilitado com sucesso!');
      return true;
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao confirmar 2FA';
      toast.error(message);
      return false;
    }
  };

  const disable2FA = async (otp: string): Promise<boolean> => {
    try {
      await api.post('/api/v2/auth/2fa/disable', { otp });

      // Atualizar o usuário buscando dados atualizados do backend
      await refreshUser();

      toast.success('2FA desabilitado com sucesso!');
      return true;
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao desabilitar 2FA';
      toast.error(message);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem(USER_CACHE_KEY);
    delete api.defaults.headers.common['Authorization'];
    setUserState(null);
    toast('Logout realizado com sucesso!');
  };

  const refreshUser = async () => {
    try {
      const response = await api.get('/api/v2/auth/me');
      setUser(response.data);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        // Token genuinely invalid — clear everything
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
      }
      // Network errors / 5xx: keep cached user — backend may be restarting
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (data: any) => {
    try {
      const response = await api.put('/api/v2/auth/profile', data);
      setUser(response.data);
      toast.success('Perfil atualizado com sucesso!');
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao atualizar perfil';
      toast.error(message);
      throw error;
    }
  };

  const requestPasswordReset = async (email: string): Promise<boolean> => {
    try {
      await api.post('/api/v2/auth/password/reset/request', { email });
      toast('Se o email estiver registrado, um link de redefinição foi enviado.');
      return true;
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao solicitar redefinição de senha';
      toast.error(message);
      return false;
    }
  };

  const confirmPasswordReset = async (token: string, newPassword: string): Promise<boolean> => {
    try {
      await api.post('/api/v2/auth/password/reset/confirm', { token, new_password: newPassword });
      toast.success('Senha redefinida com sucesso!');
      return true;
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao confirmar redefinição de senha';
      toast.error(message);
      return false;
    }
  };

  const value = {
    user,
    loading,
    login,
    loginWith2FA,
    logout,
    refreshUser,
    updateProfile,
    setup2FA,
    confirm2FA,
    disable2FA,
    requestPasswordReset,
    confirmPasswordReset,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
